import './Login.scss';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Alert, Form, InputGroup } from 'react-bootstrap';
import { IoIosEye, IoIosEyeOff } from 'react-icons/io';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { checkUserAuthorization } from '@src/utils/auth';
import {
  accessDeniedMessage,
  clearAccessDenied,
  peekAccessDenied
} from '@src/utils/auth/access-denied';
import {
  beginFederatedLogin,
  ChallengeExpiredError,
  completeNewPassword,
  InvalidCredentialsError,
  InvalidPasswordError,
  NotAuthorizedError,
  passwordSignIn,
  routeEmail,
  TooManyAttemptsError
} from '@src/utils/auth/auth-client';
import { loginRedirect, resolvePostLoginPath } from '@src/utils/auth/session';
import {
  beginOAuthFederatedLogin,
  oauthCompleteNewPassword,
  oauthPasswordSignIn,
  oauthRouteEmail,
  TxExpiredError
} from '@src/utils/auth/oauth-client';
import { reportError } from '@src/utils/errorReporting';
import { FetchError } from '@src/utils/fetch-error';
import { useSetAreYouSure } from '../../redux/modals';
import { awaitJSON, UsersLambdaFetch } from '../../utils/fetch-utils';
import { GoogleSignInButton } from './GoogleSignInButton/GoogleSignInButton';
import { LoginMode } from './login-constants';
import { getRememberedEmail, saveRememberedEmail } from './login-utils';

// Email-first sign-in (PLATFORM-988, ADR-006 Decisions 1-3, plan §5 L5 row):
//   1. EMAIL: the user enters only their email. routeEmail() tells us whether it's a federated
//      domain (straight to the IdP, no password field or Google button ever shown for it — D6)
//      or a password domain (today's checkUserAuthorization pre-check, then the password step).
//   2. PASSWORD: password sign-in, plus "Continue with Google" for non-federated domains.
//   3. NEW_PASSWORD: Cognito's NEW_PASSWORD_REQUIRED challenge.
//   4. RESET_PASSWORD: forgot-password, unchanged — still the existing /users/forgotPassword and
//      /users/confirmForgotPassword calls (D4), never routed through /auth.
const Step = {
  EMAIL: 'email',
  PASSWORD: 'password',
  NEW_PASSWORD: 'new_password',
  RESET_PASSWORD: 'reset_password'
} as const;
type StepType = (typeof Step)[keyof typeof Step];

const LOGO_SCALE = 2 / 5;
const LOGO_HEIGHT = 555;
const LOGO_WIDTH = 2108;

// Every ?auth_error=<anything> gets this one generic message. The value itself is never read
// for display — the backend never passes the IdP's or trigger's own error text through to the
// query string (interface §4), but even if it did, we don't echo it.
const AUTH_ERROR_MESSAGE = 'Sign-in failed, please try again.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const INVALID_CREDENTIALS_MESSAGE = 'Incorrect email or password.';
const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Please wait a moment and try again.';
const CHALLENGE_EXPIRED_MESSAGE = 'That took too long — please sign in again.';
const PASSWORD_RESET_NOTICE = 'Your password was updated. Log in with the new password.';

export const getReturnTo = (): string => resolvePostLoginPath();

export const hasAuthErrorParam = (): boolean =>
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('auth_error');

// Strips ?auth_error from the URL without a reload/navigation, once it's been read.
export const stripAuthErrorParam = (): void => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (!params.has('auth_error')) return;
  params.delete('auth_error');
  const query = params.toString();
  const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', cleanUrl);
};

// awaitStatus sets code and/or requestId only when the response body followed the backend's error
// contract. Without them the FetchError message is a parse failure ("Unexpected token '<' ...", a
// bare status number) from a gateway or proxy page, not text written for the user.
const isBackendContractError = (err: unknown): err is FetchError =>
  err instanceof FetchError && (err.code !== undefined || err.requestId !== undefined);

// Only these errors carry text meant for the user. Anything else (a network failure, an
// unexpected service answer, a malformed response) shows GENERIC_ERROR_MESSAGE and is reported.
const hasUserFacingMessage = (err: unknown): err is Error =>
  err instanceof InvalidCredentialsError ||
  err instanceof TooManyAttemptsError ||
  err instanceof NotAuthorizedError ||
  err instanceof InvalidPasswordError ||
  err instanceof ChallengeExpiredError ||
  err instanceof TxExpiredError ||
  isBackendContractError(err);

// Sends an error that won't be shown verbatim to reporting; the user sees the generic message.
const reportIfUnexpected = (err: unknown, operation: string): void => {
  if (hasUserFacingMessage(err)) return;
  reportError(err, { operation });
};

// Pure error-mapping helpers, exported for direct unit testing.
export const mapEmailStepError = (err: unknown): string => {
  if (hasUserFacingMessage(err) && err.message) return err.message;
  return GENERIC_ERROR_MESSAGE;
};

export const mapPasswordSignInError = (err: unknown): string => {
  if (err instanceof InvalidCredentialsError) return INVALID_CREDENTIALS_MESSAGE;
  if (err instanceof TooManyAttemptsError) return TOO_MANY_ATTEMPTS_MESSAGE;
  return mapEmailStepError(err);
};

export const mapNewPasswordError = (err: unknown): { message: string; expired: boolean } => {
  if (err instanceof ChallengeExpiredError) {
    return { message: CHALLENGE_EXPIRED_MESSAGE, expired: true };
  }
  if (err instanceof InvalidPasswordError) {
    return { message: err.policyMessage, expired: false };
  }
  return { message: mapEmailStepError(err), expired: false };
};

type LoginProps =
  | { mode?: typeof LoginMode.APP }
  | {
      mode: typeof LoginMode.AUTHORIZE;
      /** The authorization transaction from the /connect URL. */
      tx: string;
      /** Email to prefill from the /connect URL; the user can change it. */
      loginHint?: string;
      /** Called once the user is signed in for this transaction; the page moves on to consent. Keep it stable. */
      onSignedIn: () => void;
    };

export const Login = ({ ...modeProps }: LoginProps): ReactNode => {
  const authorize = modeProps.mode === LoginMode.AUTHORIZE ? modeProps : undefined;
  const tx = authorize?.tx;
  const onSignedIn = authorize?.onSignedIn;
  const [step, setStep] = useState<StepType>(Step.EMAIL);
  // A login_hint from /connect wins over the remembered email, but only an existing remembered
  // email pre-ticks the box: a hint in a URL must not opt the user into storage.
  const [email, setEmail] = useState(() => authorize?.loginHint ?? getRememberedEmail());
  const [rememberEmail, setRememberEmail] = useState(() => getRememberedEmail() !== '');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [notice, setNotice] = useState('');
  const [loginError, setLoginError] = useState<string>(() => {
    // The notices below belong to the app's own sign-in; an authorization transaction shares the
    // origin's storage on the pre-prod host but must neither show nor consume them.
    if (tx !== undefined) return '';
    // The previous session was signed out because /me said this identity isn't entitled.
    const denied = peekAccessDenied();
    if (denied) return accessDeniedMessage(denied.email);
    return hasAuthErrorParam() ? AUTH_ERROR_MESSAGE : '';
  });

  useEffect(() => {
    // Both notices show once: drop them after the first render that read them, not in the state
    // initializer (StrictMode runs that twice, and the second run would find nothing).
    if (tx !== undefined) return;
    clearAccessDenied();
    stripAuthErrorParam();
    // Run once on mount only — reading the initial URL, not reacting to later state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAreYouSure = useSetAreYouSure();

  const resetPassword = useCallback(async () => {
    // Where to return if the verification email can't be sent.
    const returnStep = step === Step.PASSWORD ? Step.PASSWORD : Step.EMAIL;
    await setAreYouSure({
      title: 'Confirm Forgot Password',
      message: 'You are about to reset your password, are you sure?',
      intent: Intent.WARNING,
      cancelText: 'Never mind',
      okayText: 'Yes',
      onOkay: async () => {
        if (email.indexOf('@') > 0) {
          setStep(Step.RESET_PASSWORD);
          try {
            // skipAuthRecovery: no session exists yet here (mid-login) — see
            // checkUserAuthorization's rationale in auth.ts.
            const res = await UsersLambdaFetch('/forgotPassword', {
              method: 'POST',
              body: { username: email },
              skipAuthRecovery: true,
              // No session exists mid-login, so there is no token for buildAuthHeader to attach.
              authenticate: false
            });
            await awaitJSON(res);
          } catch (e) {
            // The request to send the verification code failed — fall back to the
            // step the user came from instead of stranding the user on a code-entry screen for
            // an email that was never sent.
            reportIfUnexpected(e, 'login_forgot_password');
            setLoginError(mapEmailStepError(e));
            setStep(returnStep);
          }
        } else {
          setLoginError('Invalid email address');
        }
      }
    });
  }, [email, setAreYouSure, step]);

  const handleEmailSubmit = useCallback(
    async (e?: SyntheticEvent) => {
      e?.preventDefault();
      setIsSubmitting(true);
      setLoginError('');
      try {
        const result = tx === undefined ? await routeEmail(email) : await oauthRouteEmail(email);
        if (result.method === 'federated') {
          saveRememberedEmail(email, rememberEmail);
          if (tx === undefined) {
            beginFederatedLogin({
              provider: result.provider,
              loginHint: email,
              returnTo: getReturnTo()
            });
          } else {
            beginOAuthFederatedLogin({ tx, provider: result.provider, loginHint: email });
          }
          // Navigates the page away — leave isSubmitting on rather than resetting state on an
          // unmounting component.
          return;
        }
        // The app-API pre-check is a cross-origin call with no session; the front's route answer
        // is the only account check on the authorize path.
        if (tx === undefined) {
          await checkUserAuthorization(email);
        }
        saveRememberedEmail(email, rememberEmail);
        setStep(Step.PASSWORD);
        setIsSubmitting(false);
      } catch (err) {
        reportIfUnexpected(err, 'login_email_step');
        setLoginError(mapEmailStepError(err));
        setIsSubmitting(false);
      }
    },
    [email, rememberEmail, tx]
  );

  const handlePasswordSubmit = useCallback(
    async (e?: SyntheticEvent) => {
      e?.preventDefault();
      setIsSubmitting(true);
      setLoginError('');
      setNotice('');
      try {
        const result =
          tx === undefined
            ? await passwordSignIn(email, password)
            : await oauthPasswordSignIn(tx, email, password);
        if (result.status === 'new_password_required') {
          setStep(Step.NEW_PASSWORD);
          setIsSubmitting(false);
          return;
        }
        if (onSignedIn) {
          onSignedIn();
          return;
        }
        loginRedirect();
      } catch (err) {
        reportIfUnexpected(err, 'login_password_step');
        setLoginError(mapPasswordSignInError(err));
        setIsSubmitting(false);
      }
    },
    [email, password, tx, onSignedIn]
  );

  const handleNewPasswordSubmit = useCallback(
    async (e?: SyntheticEvent) => {
      e?.preventDefault();
      setIsSubmitting(true);
      setLoginError('');
      try {
        if (tx === undefined) {
          await completeNewPassword(newPassword);
        } else {
          await oauthCompleteNewPassword(tx, newPassword);
        }
        if (onSignedIn) {
          onSignedIn();
          return;
        }
        loginRedirect();
      } catch (err) {
        reportIfUnexpected(err, 'login_new_password_step');
        const { message, expired } = mapNewPasswordError(err);
        if (expired) {
          setStep(Step.PASSWORD);
          setPassword('');
        }
        setLoginError(message);
        setIsSubmitting(false);
      }
    },
    [newPassword, tx, onSignedIn]
  );

  const handleResetPasswordSubmit = useCallback(
    async (e?: SyntheticEvent) => {
      e?.preventDefault();
      setIsSubmitting(true);
      setLoginError('');
      try {
        // skipAuthRecovery: no session exists yet here (mid-login) — see
        // checkUserAuthorization's rationale in auth.ts.
        const res = await UsersLambdaFetch('/confirmForgotPassword', {
          method: 'POST',
          body: {
            username: email,
            confirmationCode: verificationCode,
            password: newPassword
          },
          skipAuthRecovery: true,
          // No session exists mid-login, so there is no token for buildAuthHeader to attach.
          authenticate: false
        });
        await awaitJSON(res);
        if (tx !== undefined) {
          // The reset doesn't sign the user in, and the app root isn't where this transaction
          // continues: return to the password step for the same transaction.
          setPassword('');
          setNewPassword('');
          setVerificationCode('');
          setNotice(PASSWORD_RESET_NOTICE);
          setStep(Step.PASSWORD);
          setIsSubmitting(false);
          return;
        }
        loginRedirect();
      } catch (err) {
        reportIfUnexpected(err, 'login_reset_password_step');
        setLoginError(mapEmailStepError(err));
        setIsSubmitting(false);
      }
    },
    [email, verificationCode, newPassword, tx]
  );

  const handleGoogleClick = useCallback(() => {
    setIsGoogleSubmitting(true);
    setLoginError('');
    if (tx === undefined) {
      beginFederatedLogin({
        provider: 'accounts.google.com',
        loginHint: email,
        returnTo: getReturnTo()
      });
    } else {
      beginOAuthFederatedLogin({ tx, provider: 'accounts.google.com', loginHint: email });
    }
  }, [email, tx]);

  const stepInner = useMemo(() => {
    switch (step) {
      case Step.NEW_PASSWORD:
        return (
          <NewPasswordStepInner
            email={email}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            loginError={loginError}
            isSubmitting={isSubmitting}
            onSubmit={handleNewPasswordSubmit}
          />
        );
      case Step.RESET_PASSWORD:
        return (
          <ResetPasswordStepInner
            email={email}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            loginError={loginError}
            isSubmitting={isSubmitting}
            onSubmit={handleResetPasswordSubmit}
          />
        );
      case Step.PASSWORD:
        return (
          <PasswordStepInner
            email={email}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            loginError={loginError}
            notice={notice}
            isSubmitting={isSubmitting}
            isGoogleSubmitting={isGoogleSubmitting}
            onSubmit={handlePasswordSubmit}
            onGoogleClick={handleGoogleClick}
            onForgotPassword={resetPassword}
          />
        );
      case Step.EMAIL:
      default:
        return (
          <EmailStepInner
            email={email}
            setEmail={setEmail}
            rememberEmail={rememberEmail}
            setRememberEmail={setRememberEmail}
            loginError={loginError}
            isSubmitting={isSubmitting}
            onSubmit={handleEmailSubmit}
            onForgotPassword={resetPassword}
          />
        );
    }
  }, [
    confirmPassword,
    email,
    handleEmailSubmit,
    handleGoogleClick,
    handleNewPasswordSubmit,
    handlePasswordSubmit,
    handleResetPasswordSubmit,
    isGoogleSubmitting,
    isSubmitting,
    loginError,
    newPassword,
    notice,
    password,
    rememberEmail,
    resetPassword,
    showPassword,
    step,
    verificationCode
  ]);

  return (
    <div className='bpm-login'>
      <img
        className='agency-logo'
        alt='Bliss Point by Tinuiti'
        src='https://cdn.blisspointmedia.com/logos/login_logo.svg'
        height={LOGO_HEIGHT * LOGO_SCALE}
        width={LOGO_WIDTH * LOGO_SCALE}
        data-testid='login-agency-logo'
      />
      <div className='bpm-login-inner'>
        <Form className='login-form' onSubmit={e => e.preventDefault()}>
          {stepInner}
        </Form>
      </div>
    </div>
  );
};

interface ShowPasswordIconBtnProps {
  showPassword: boolean;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
}

const ShowPasswordIconBtn = ({ showPassword, setShowPassword }: ShowPasswordIconBtnProps) => (
  <div className='show-password-icon-btn'>
    <IconButton
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      onClick={() => setShowPassword(current => !current)}
      data-testid='login-show-password-button'
    >
      {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
    </IconButton>
  </div>
);

interface EmailStepInnerProps {
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  rememberEmail: boolean;
  setRememberEmail: Dispatch<SetStateAction<boolean>>;
  loginError: string;
  isSubmitting: boolean;
  onSubmit: (e?: SyntheticEvent) => void;
  onForgotPassword: () => void;
}

// A quick client-side shape check only — routing authority is entirely server-side (D3: the
// domain -> provider map lives in bpm-lambdas). This just gates the Continue button.
const looksLikeEmail = (value: string): boolean => value.includes('@') && value.indexOf('@') > 0;

const EmailStepInner = ({
  email,
  setEmail,
  rememberEmail,
  setRememberEmail,
  loginError,
  isSubmitting,
  onSubmit,
  onForgotPassword
}: EmailStepInnerProps) => (
  <>
    <InputGroup size='lg'>
      <InputGroup.Text className='login-prepend'>Email</InputGroup.Text>
      <Form.Control
        placeholder='Email'
        value={email}
        onChange={e => setEmail(e.target.value)}
        data-testid='login-email-input'
      />
    </InputGroup>
    <Checkbox
      id='login-remember-email'
      label='Remember my email'
      checked={rememberEmail}
      onChange={e => setRememberEmail(e.target.checked)}
      data-testid='login-remember-email-checkbox'
    />
    {loginError && <Alert variant='danger'>{loginError}</Alert>}
    <Button
      className='login-button'
      disabled={isSubmitting || !looksLikeEmail(email)}
      loading={isSubmitting}
      size={Size.LG}
      onClick={onSubmit}
      type='submit'
      data-testid='login-continue-button'
    >
      Continue
    </Button>
    <Button
      className='forgot-password'
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.LG}
      onClick={onForgotPassword}
      data-testid='login-forgot-password-button'
    >
      Forgot Password?
    </Button>
  </>
);

interface PasswordStepInnerProps {
  email: string;
  password: string;
  setPassword: Dispatch<SetStateAction<string>>;
  showPassword: boolean;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
  loginError: string;
  notice: string;
  isSubmitting: boolean;
  isGoogleSubmitting: boolean;
  onSubmit: (e?: SyntheticEvent) => void;
  onGoogleClick: () => void;
  onForgotPassword: () => void;
}

const PasswordStepInner = ({
  email,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  loginError,
  notice,
  isSubmitting,
  isGoogleSubmitting,
  onSubmit,
  onGoogleClick,
  onForgotPassword
}: PasswordStepInnerProps) => (
  <>
    {notice && <Alert variant='success'>{notice}</Alert>}
    <InputGroup size='lg'>
      <InputGroup.Text className='login-prepend'>Email</InputGroup.Text>
      <Form.Control placeholder='Email' value={email} readOnly data-testid='login-email-input' />
    </InputGroup>
    <InputGroup size='lg'>
      <InputGroup.Text className='login-prepend'>Password</InputGroup.Text>
      <Form.Control
        type={showPassword ? 'text' : 'password'}
        placeholder='Password'
        value={password}
        onChange={e => setPassword(e.target.value)}
        data-testid='login-password-input'
      />
      <ShowPasswordIconBtn showPassword={showPassword} setShowPassword={setShowPassword} />
    </InputGroup>
    {loginError && <Alert variant='danger'>{loginError}</Alert>}
    <Button
      className='login-button'
      disabled={isSubmitting || !password}
      loading={isSubmitting}
      size={Size.LG}
      onClick={onSubmit}
      type='submit'
      data-testid='login-submit-button'
    >
      Log In
    </Button>
    <GoogleSignInButton submitting={isGoogleSubmitting} onClick={onGoogleClick} />
    <Button
      className='forgot-password'
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.LG}
      onClick={onForgotPassword}
      data-testid='login-forgot-password-button'
    >
      Forgot Password?
    </Button>
  </>
);

interface NewPasswordStepInnerProps {
  email: string;
  newPassword: string;
  setNewPassword: Dispatch<SetStateAction<string>>;
  confirmPassword: string;
  setConfirmPassword: Dispatch<SetStateAction<string>>;
  showPassword: boolean;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
  loginError: string;
  isSubmitting: boolean;
  onSubmit: (e?: SyntheticEvent) => void;
}

// 8+ chars, uppercase, lowercase, a number and a special character — the pool's password policy.
const PASSWORD_REGEXP = /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})/;

const NewPasswordStepInner = ({
  email,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  loginError,
  isSubmitting,
  onSubmit
}: NewPasswordStepInnerProps) => {
  const newPasswordIsValid = useMemo(
    () => newPassword === confirmPassword && PASSWORD_REGEXP.test(newPassword),
    [confirmPassword, newPassword]
  );
  const hasNecessaryInputs = newPassword && confirmPassword;

  return (
    <>
      <InputGroup size='lg'>
        <InputGroup.Text className='login-prepend'>Email</InputGroup.Text>
        <Form.Control placeholder='Email' value={email} readOnly data-testid='login-email-input' />
      </InputGroup>
      <InputGroup size='lg'>
        <InputGroup.Text className='login-prepend'>New Password</InputGroup.Text>
        <Form.Control
          type={showPassword ? 'text' : 'password'}
          placeholder='New Password'
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          data-testid='login-new-password-input'
        />
      </InputGroup>
      <InputGroup size='lg'>
        <InputGroup.Text className='login-prepend'>Confirm Password</InputGroup.Text>
        <Form.Control
          type={showPassword ? 'text' : 'password'}
          placeholder='Confirm Password'
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          data-testid='login-confirm-password-input'
        />
        <ShowPasswordIconBtn showPassword={showPassword} setShowPassword={setShowPassword} />
      </InputGroup>
      {loginError && <Alert variant='danger'>{loginError}</Alert>}
      {newPassword.length > 0 && !newPasswordIsValid && (
        <Alert variant='danger'>
          Your new password must be at least 8 characters long and contain an uppercase letter,
          lowercase letter, number, and special character.
        </Alert>
      )}
      <Button
        className='login-button'
        disabled={isSubmitting || !newPasswordIsValid || !hasNecessaryInputs}
        loading={isSubmitting}
        size={Size.LG}
        onClick={onSubmit}
        type='submit'
        data-testid='login-submit-button'
      >
        Change Password & Log In
      </Button>
    </>
  );
};

interface ResetPasswordStepInnerProps {
  email: string;
  newPassword: string;
  setNewPassword: Dispatch<SetStateAction<string>>;
  verificationCode: string;
  setVerificationCode: Dispatch<SetStateAction<string>>;
  showPassword: boolean;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
  loginError: string;
  isSubmitting: boolean;
  onSubmit: (e?: SyntheticEvent) => void;
}

const ResetPasswordStepInner = ({
  email,
  newPassword,
  setNewPassword,
  verificationCode,
  setVerificationCode,
  showPassword,
  setShowPassword,
  loginError,
  isSubmitting,
  onSubmit
}: ResetPasswordStepInnerProps) => {
  const hasNecessaryInputs = newPassword && verificationCode;

  return (
    <>
      <Alert variant='info'>
        Please check your email for a verification code from automation@tinuiti.com and enter it in
        the &quot;Verification Code&quot; field along with a new password. Your new password must be
        at least 8 characters long and contain an uppercase letter, lowercase letter, number, and
        special character.
      </Alert>
      <InputGroup size='lg'>
        <InputGroup.Text className='login-prepend'>Email</InputGroup.Text>
        <Form.Control placeholder='Email' value={email} readOnly data-testid='login-email-input' />
      </InputGroup>
      <InputGroup size='lg'>
        <InputGroup.Text className='login-prepend'>New Password</InputGroup.Text>
        <Form.Control
          type={showPassword ? 'text' : 'password'}
          placeholder='New Password'
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          data-testid='login-new-password-input'
        />
        <ShowPasswordIconBtn showPassword={showPassword} setShowPassword={setShowPassword} />
      </InputGroup>
      <InputGroup size='lg'>
        <InputGroup.Text className='login-prepend'>Verification Code</InputGroup.Text>
        <Form.Control
          placeholder='Code'
          value={verificationCode}
          onChange={e => setVerificationCode(e.target.value)}
          data-testid='login-verification-code-input'
        />
      </InputGroup>
      {loginError && <Alert variant='danger'>{loginError}</Alert>}
      <Button
        className='login-button'
        disabled={isSubmitting || !hasNecessaryInputs}
        loading={isSubmitting}
        size={Size.LG}
        onClick={onSubmit}
        type='submit'
        data-testid='login-submit-button'
      >
        Change Password & Log In
      </Button>
    </>
  );
};
